import React, { useState } from 'react';
import { X, CheckCircle, Edit3, Save, Share2, Monitor, Archive, Zap } from 'lucide-react';
import AuthModal from './AuthModal';

function LoginRequiredModal({ isOpen, onClose, onLogin, onRegister, onLoginSuccess, onRegisterSuccess, feature = 'interface' }) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');

  if (!isOpen) return null;

  // Configurações contextuais baseadas na funcionalidade
  const getFeatureConfig = (featureType) => {
    const configs = {
      interface: {
        title: "Desbloqueie o Editor Completo",
        subtitle: "Crie roadmaps com mais agilidade usando nossa interface interativa.",
        icon: Edit3,
        iconColor: "text-amber-600",
        iconBg: "bg-amber-100",
        benefits: [
          {
            icon: Monitor,
            text: "Editor profissional, direto no navegador",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50"
          },
          {
            icon: Archive,
            text: "Continue de onde parou: seus roadmaps ficam salvos na conta",
            iconColor: "text-green-600",
            iconBg: "bg-green-50"
          },
          {
            icon: Zap,
            text: "Explore primeiro as novidades da plataforma",
            iconColor: "text-purple-600",
            iconBg: "bg-purple-50"
          }
        ],
        alternativeTitle: "Alternativa rápida",
        alternativeText: "Use o upload de arquivo para criar roadmaps básicos sem login."
      },
      savedRoadmaps: {
        title: "Acesse Seus Roadmaps Salvos",
        subtitle: "Gerencie e edite todos os seus roadmaps em um só lugar",
        icon: Save,
        iconColor: "text-blue-600",
        iconBg: "bg-blue-100",
        benefits: [
          {
            icon: Archive,
            text: "Acesso a todos os roadmaps salvos",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50"
          },
          {
            icon: Edit3,
            text: "Edição e atualização a qualquer momento",
            iconColor: "text-green-600",
            iconBg: "bg-green-50"
          },
          {
            icon: CheckCircle,
            text: "Histórico de versões e backup automático",
            iconColor: "text-purple-600",
            iconBg: "bg-purple-50"
          }
        ],
        alternativeTitle: "Sem conta ainda?",
        alternativeText: "Crie roadmaps temporários com upload de arquivo."
      },
      sharing: {
        title: "Compartilhe Seus Roadmaps",
        subtitle: "Colabore e compartilhe seus roadmaps com sua equipe",
        icon: Share2,
        iconColor: "text-green-600",
        iconBg: "bg-green-100",
        benefits: [
          {
            icon: Share2,
            text: "Links de compartilhamento personalizados",
            iconColor: "text-green-600",
            iconBg: "bg-green-50"
          },
          {
            icon: CheckCircle,
            text: "Controle de permissões de visualização",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50"
          },
          {
            icon: Monitor,
            text: "Colaboração em tempo real com equipe",
            iconColor: "text-purple-600",
            iconBg: "bg-purple-50"
          }
        ],
        alternativeTitle: "Compartilhamento básico",
        alternativeText: "Baixe como imagem e compartilhe manualmente."
      },
      premium: {
        title: "Funcionalidades Premium",
        subtitle: "Desbloqueie todo o potencial da plataforma",
        icon: CheckCircle,
        iconColor: "text-purple-600",
        iconBg: "bg-purple-100",
        benefits: [
          {
            icon: Zap,
            text: "Todos os recursos avançados disponíveis",
            iconColor: "text-purple-600",
            iconBg: "bg-purple-50"
          },
          {
            icon: CheckCircle,
            text: "Suporte prioritário e atualizações",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50"
          },
          {
            icon: Edit3,
            text: "Acesso antecipado a novas funcionalidades",
            iconColor: "text-green-600",
            iconBg: "bg-green-50"
          }
        ],
        alternativeTitle: "Funcionalidades básicas",
        alternativeText: "Continue com as funcionalidades gratuitas limitadas."
      },
      saveRoadmap: {
        title: "Salve seu Roadmap",
        subtitle: "Mantenha seu trabalho seguro e acesse de qualquer lugar",
        icon: Save,
        iconColor: "text-green-600",
        iconBg: "bg-green-100",
        benefits: [
          {
            icon: Archive,
            text: "Seu roadmap ficará salvo permanentemente",
            iconColor: "text-green-600",
            iconBg: "bg-green-50"
          },
          {
            icon: Edit3,
            text: "Continue editando quando quiser",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50"
          },
          {
            icon: Share2,
            text: "Compartilhe com sua equipe facilmente",
            iconColor: "text-purple-600",
            iconBg: "bg-purple-50"
          }
        ],
        alternativeTitle: "Não quer criar conta agora?",
        alternativeText: "Exporte para XLSX ou PNG não perder seu trabalho."
      }
    };
    
    return configs[featureType] || configs.interface;
  };

  const config = getFeatureConfig(feature);

  const handleLoginClick = () => {
    setAuthModalMode('login');
    setShowAuthModal(true);
  };

  const handleRegisterClick = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    // Após fechar o modal de auth, fechar o modal principal também
    onClose();
  };

  const handleAuthModalSuccess = (user, mode) => {
    setShowAuthModal(false);
    onClose();
    
    // Chama callback específico baseado no modo
    if (mode === 'login' && onLoginSuccess) {
      onLoginSuccess(user);
    } else if (mode === 'register' && onRegisterSuccess) {
      onRegisterSuccess(user);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-fade-in relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="p-5 pb-3">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${config.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <config.icon className={`w-5 h-5 ${config.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 id="login-modal-title" className="text-lg font-semibold text-gray-900 leading-tight">
                  {config.title}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {config.subtitle}
                </p>
              </div>
            </div>

            {/* Benefits - Visual e Atrativo */}
            <div className="space-y-2">
              {config.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200">
                  <div className={`w-7 h-7 ${benefit.iconBg} rounded-full flex items-center justify-center flex-shrink-0 border border-white shadow-sm`}>
                    <benefit.icon className={`w-3.5 h-3.5 ${benefit.iconColor}`} />
                  </div>
                  <span className="text-xs text-gray-800 font-medium leading-relaxed">
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-5 py-3 bg-gray-50 space-y-2">
            {/* Primary Actions */}
            <div className="flex flex-col space-y-2">
              <button
                onClick={handleLoginClick}
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                aria-label="Fazer login para acessar recursos completos"
              >
                Fazer Login
              </button>
              
              <button
                onClick={handleRegisterClick}
                className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                aria-label="Criar nova conta para acessar recursos completos"
              >
                Criar Conta Grátis
              </button>
            </div>

            {/* Alternative Option - Menor Relevância */}
            <div className="pt-2 border-t border-gray-200">
              <div className="bg-gray-50 border border-gray-200 px-3 py-2 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 text-xs mt-0.5" role="img" aria-label="Informação">💡</span>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-0.5">{config.alternativeTitle}</h5>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {config.alternativeText}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Continue Option */}
            <div className="pt-1">
              <div className="text-center">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-500 text-xs hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg transition-colors duration-200"
                  aria-label="Fechar modal e continuar com upload de arquivo"
                >
                  Continuar com upload de arquivo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Auth Modal Integration - Renderizado independentemente com z-index maior */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleAuthModalClose}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <AuthModal
                isOpen={showAuthModal}
                onClose={handleAuthModalClose}
                onSuccess={handleAuthModalSuccess}
                initialMode={authModalMode}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LoginRequiredModal;
